import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError, deleteAdmin, grantAdmin, listAdmins } from './api/client';
import type { AdminResponse, AdminRole } from './api/types';

// The roles the backend accepts (AdminRole): Owner is a superset of Admin.
const ADMIN_ROLES: readonly AdminRole[] = ['Admin', 'Owner'];

function describeError(e: unknown): string {
  return e instanceof ApiError ? `${e.body.code}: ${e.body.description}` : 'Request failed';
}

const left = { textAlign: 'left' } as const;

/**
 * Manage one tenant's admin allowlist (list / grant by email + role / revoke). Auth is deferred
 * (ADR-011), so a grant is an allowlist entry — the later auth epic resolves it to a real login.
 */
export function AdminsPanel({ tenant }: { tenant: string }) {
  const [admins, setAdmins] = useState<AdminResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // grant form
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AdminRole>('Admin');

  async function load(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setAdmins(await listAdmins(tenant));
    } catch (e: unknown) {
      setError(describeError(e));
      setAdmins([]);
    } finally {
      setBusy(false);
    }
  }

  // Reload whenever the acting-as tenant changes.
  useEffect(() => {
    void load();
  }, [tenant]);

  async function onGrant(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await grantAdmin(tenant, email, { role });
      setEmail('');
      await load();
    } catch (err: unknown) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  async function onRevoke(adminEmail: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await deleteAdmin(tenant, adminEmail);
      await load();
    } catch (err: unknown) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  return (
    <section>
      <h2>
        Admins for &ldquo;{tenant}&rdquo;{' '}
        <button onClick={() => void load()} disabled={busy}>
          Reload
        </button>
      </h2>

      {error !== null && (
        <p role="alert" style={{ color: '#b00020' }}>
          {error}
        </p>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={left}>Email</th>
            <th style={left}>Role</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {admins.length === 0 ? (
            <tr>
              <td colSpan={3}>
                <em>No admins for &ldquo;{tenant}&rdquo;.</em>
              </td>
            </tr>
          ) : (
            admins.map((a) => (
              <tr key={a.email}>
                <td>{a.email}</td>
                <td>{a.role}</td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => void onRevoke(a.email)} disabled={busy}>
                    Revoke
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <h3>Grant admin</h3>
      <form
        onSubmit={(e) => void onGrant(e)}
        style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}
      >
        <input
          required
          type="email"
          placeholder="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <select value={role} onChange={(e) => setRole(e.currentTarget.value as AdminRole)}>
          {ADMIN_ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <button type="submit" disabled={busy}>
          Grant
        </button>
      </form>
    </section>
  );
}
